import React from 'react';
import { Typography, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { IconClockHour4, IconGavel, IconHandStop, IconAlertTriangle, IconCheck } from '@tabler/icons-react';
import { IBookingDispute } from '../interfaces/heavyDutyMachinery/IBookingDispute';
import {
  DISPUTE_STATUS_LABEL_KEYS,
  DISPUTE_STATUS_TONES,
  defaultOutcomeKey,
  hoursToRespond,
  isClaimant,
  latestOffer,
} from '../helpers/disputeHelper';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';
import { formatCurrency } from '../helpers/currencyHelper';
import { pillToneStyle, CORAL, CORAL_SOFT_BG, TEAL, TEAL_SOFT_BG } from '../theme/creamyTokens';

const { Title, Text } = Typography;

interface DisputeStatusCardProps {
  dispute: IBookingDispute;
  currencyCode: string;
  userId?: string;
}

/**
 * Where this claim stands, what is on the table, and -- the part that
 * actually does the work -- what happens if nobody does anything.
 *
 * That last line is deliberately the loudest thing on the card. Most
 * disputes are not resolved by anyone deciding them; they are resolved by
 * a deadline somebody could read. A default you were warned about in plain
 * words is one you act on in time, and one you can't reasonably complain
 * about afterwards.
 */
const DisputeStatusCard: React.FC<DisputeStatusCardProps> = ({ dispute, currencyCode, userId }) => {
  const { t, i18n } = useTranslation();
  const { token } = theme.useToken();

  const hoursLeft = hoursToRespond(dispute);
  // Decided AND paid. settledAt is only ever stamped once the money actually
  // moved, so this is the one honest test -- the status alone says Settled
  // either way.
  const settled = !!dispute.settledAt;
  const offer = latestOffer(dispute);
  const defaultKey = defaultOutcomeKey(dispute, userId);

  // Three amounts can be the headline, and only one is true at a time:
  // what was claimed, what is currently offered, and -- once it's over --
  // what actually moved.
  const headline =
    dispute.resolvedAmount != null
      ? { labelKey: TRANSLATIONS.DISPUTE_RESOLVED_LABEL, amount: dispute.resolvedAmount }
      : offer
        ? { labelKey: TRANSLATIONS.DISPUTE_CURRENT_OFFER_LABEL, amount: offer.amount }
        : { labelKey: TRANSLATIONS.DISPUTE_CLAIMED_LABEL, amount: dispute.claimedAmount };

  return (
    <div
      style={{
        background: token.colorBgContainer,
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: 16,
        padding: 16,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <span
          style={{
            display: 'inline-flex',
            padding: '3px 10px',
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 600,
            ...pillToneStyle(DISPUTE_STATUS_TONES[dispute.status], token),
          }}
        >
          {translate(t, DISPUTE_STATUS_LABEL_KEYS[dispute.status])}
        </span>
        {hoursLeft !== undefined && (
          <Text
            style={{
              fontSize: 13,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              color: hoursLeft === 0 ? token.colorError : token.colorTextSecondary,
            }}
          >
            <IconClockHour4 size={14} />
            {hoursLeft === 0
              ? translate(t, TRANSLATIONS.DISPUTE_DEADLINE_PASSED)
              : translate(t, TRANSLATIONS.DISPUTE_HOURS_LEFT, { hours: hoursLeft })}
          </Text>
        )}
      </div>

      <Text type="secondary" style={{ fontSize: 13, display: 'block' }}>
        {translate(t, headline.labelKey)}
      </Text>
      <Title level={3} style={{ margin: '2px 0 0' }}>
        {formatCurrency(headline.amount, currencyCode, i18n.language)}
      </Title>

      {/* The claimant's own account, shown to both sides. Their words, not
          a summary of them -- a paraphrase in a dispute is an opinion. */}
      <Text style={{ fontSize: 14, display: 'block', marginTop: 12 }}>{dispute.reason}</Text>
      {dispute.response && (
        <div
          style={{
            marginTop: 12,
            paddingTop: 12,
            borderTop: `1px solid ${token.colorBorderSecondary}`,
          }}
        >
          <Text type="secondary" style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
            <IconHandStop size={14} />
            {translate(t, isClaimant(dispute, userId) ? TRANSLATIONS.DISPUTE_OFFER_BY_THEM : TRANSLATIONS.DISPUTE_OFFER_BY_ME)}
          </Text>
          <Text style={{ fontSize: 14, display: 'block', marginTop: 4 }}>{dispute.response}</Text>
        </div>
      )}

      {/* What happens on its own. See this component's own doc comment for
          why this gets a tinted block rather than a quiet grey line. */}
      {defaultKey && (
        <div
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'flex-start',
            marginTop: 14,
            padding: 12,
            borderRadius: 12,
            background: CORAL_SOFT_BG,
          }}
        >
          <span style={{ display: 'flex', flexShrink: 0, color: CORAL, marginTop: 1 }}>
            <IconGavel size={16} />
          </span>
          <Text style={{ fontSize: 13 }}>{translate(t, defaultKey)}</Text>
        </div>
      )}

      {dispute.status === 'UnderReview' && (
        <Text type="secondary" style={{ fontSize: 13, display: 'block', marginTop: 12 }}>
          {translate(t, TRANSLATIONS.DISPUTE_UNDER_REVIEW_BODY)}
        </Text>
      )}

      {/* Decided is not the same as paid, and this is the one place either
          party can find out which they got. Only ever rendered once a real
          amount was decided: a withdrawal and a claim that failed both close
          at zero, and there is nothing to have moved. */}
      {(dispute.resolvedAmount ?? 0) > 0 && (settled || dispute.settlementError) && (
        <div
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'flex-start',
            marginTop: 14,
            padding: 12,
            borderRadius: 12,
            background: settled ? TEAL_SOFT_BG : CORAL_SOFT_BG,
          }}
        >
          <span style={{ display: 'flex', flexShrink: 0, color: settled ? TEAL : CORAL, marginTop: 1 }}>
            {settled ? <IconCheck size={16} /> : <IconAlertTriangle size={16} />}
          </span>
          <div style={{ minWidth: 0 }}>
            <Text strong style={{ fontSize: 13, display: 'block' }}>
              {translate(t, settled ? TRANSLATIONS.DISPUTE_SETTLED_LABEL : TRANSLATIONS.DISPUTE_NOT_SETTLED_TITLE)}
            </Text>
            <Text style={{ fontSize: 13 }}>
              {settled
                ? translate(t, TRANSLATIONS.DISPUTE_SETTLED_BODY)
                : /* The backend's own words. On the commonest case -- the host
                     won, so it comes out of the fianza -- that sentence is an
                     explanation rather than a failure, and it reads better
                     than anything this screen could infer. */
                  dispute.settlementError}
            </Text>
          </div>
        </div>
      )}
    </div>
  );
};

export default DisputeStatusCard;
